import { c as _c } from "react/compiler-runtime";
import type { Analytics } from '@segment/analytics-next';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { createContext, useMemo, useContext, useEffect, useState, type JSX } from 'react';
import * as React from 'react';
import type { SegmentClientOptions } from './client';
import { SegmentClient } from './client';
interface ClientResponse {
  client: SegmentClient;
  isReady: boolean;
}

/**
 * This is the context object that useAnalytics will be grabbing the client from.
 * When you're trying to mock analytics calls, you should pass a fake value here.
 */
export const SegmentContext = createContext<ClientResponse | undefined>(undefined);

/**
 * The provider props. The API key is for the Segment source.
 */

interface SegmentProviderProps {
  apiKey: string;
  children: React.ReactNode;
  clientOptions: SegmentClientOptions;
}

/**
 * Create a Segment client. The client will maintain it's identity while the options are unchanged between renders.
 * @param options
 */
export function useSegmentClient(apiKey, options) {
  const $ = _c(17);
  const [analytics, setAnalytics] = useState();
  const [isReady, setIsReady] = useState(false);
  const {
    anonymizeIp,
    debug,
    defaultTrackProperties,
    timeout,
    enabled: t0
  } = options;
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let segmentClient;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    segmentClient = new SegmentClient();
    segmentClient.emitter.on("initialize", () => {
      setIsReady(true);
    });
    $[0] = segmentClient;
  } else {
    segmentClient = $[0];
  }
  t1 = segmentClient;
  const client = t1;
  let t2;
  if ($[1] !== apiKey || $[2] !== enabled) {
    t2 = () => {
      if (!enabled) {
        return;
      }
      AnalyticsBrowser.load({
        writeKey: apiKey
      }).then(result => {
        const [response] = result;
        setAnalytics(response);
      }).catch(_temp);
    };
    $[1] = apiKey;
    $[2] = enabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== apiKey) {
    t3 = [apiKey];
    $[4] = apiKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== anonymizeIp || $[7] !== debug || $[8] !== defaultTrackProperties || $[9] !== timeout) {
    t4 = () => {
      if (client) {
        client.setOptions({
          anonymizeIp,
          debug,
          defaultTrackProperties,
          timeout
        });
      }
    };
    t5 = [client, anonymizeIp, debug, defaultTrackProperties, timeout];
    $[6] = anonymizeIp;
    $[7] = debug;
    $[8] = defaultTrackProperties;
    $[9] = timeout;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[12] !== analytics) {
    t6 = () => {
      if (analytics) {
        client.initialize(analytics);
      }
    };
    t7 = [client, analytics];
    $[12] = analytics;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== isReady) {
    t8 = {
      client,
      isReady
    };
    $[15] = isReady;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

/**
 * Load the Segment snippet and add it to the app context. This client will be available before the script
 * has finished loading so that it doesn't block page rendering.
 * @param props SegmentProviderProps
 */
function _temp(err) {
  console.warn("There was a problem loading the Segment snippet. Skipping.", err);
}
export function SegmentClientProvider(props) {
  const $ = _c(3);
  const {
    apiKey,
    children,
    clientOptions
  } = props;
  const client = useSegmentClient(apiKey, clientOptions);
  let t0;
  if ($[0] !== children || $[1] !== client) {
    t0 = <SegmentContext.Provider value={client}>{children}</SegmentContext.Provider>;
    $[0] = children;
    $[1] = client;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * Return the Segment client added by <SegmentProvider>. This provider must be added higher up in the React tree to
 * be able to use this hook.
 */
export function useSegmentClientContext() {
  const result = useContext(SegmentContext);
  if (!result) {
    throw new Error("The useSegment hook needs <SegmentProvider> to be present higher in the React tree.");
  }
  return result;
}